import React, { PropsWithChildren, createContext, useCallback, useContext, useMemo, useState } from 'react';
import { useNavigate } from 'react-router-dom';
import { useExpenses } from './ExpensesContext';
import { ExtractedExpenseData, ProcessingReceipt } from '../types/receipt';

export interface SuggestedExpense {
  id: string;
  data: ExtractedExpenseData;
  receiptUrl?: string;
  originalFileName?: string;
  isAdded: boolean;
}

interface SuggestedExpensesContextType {
  suggestedExpenses: SuggestedExpense[];
  currentIndex: number;
  currentExpense: SuggestedExpense | undefined;
  totalItems: number;
  loading: boolean;
  navigate: (direction: 'prev' | 'next') => void;
  goTo: (index: number) => void;
  addExpense: (id: string, formData: ExpenseFormData) => Promise<void>;
  addAllExpenses: () => Promise<void>;
  setSuggestedFromReceipts: (receipts: ProcessingReceipt[]) => void;
  clear: () => void;
}

export interface ExpenseFormData {
  tripId: string;
  category: string;
  description: string;
  amount: number;
  currency: string;
  expense_date: string;
  base_amount?: number;
  tax?: number;
  tip?: number;
  fees?: number;
  merchant?: string;
  notes?: string;
  is_planned: boolean;
  receipt_url?: string;
  original_file_name?: string;
}

const SuggestedExpensesContext = createContext<SuggestedExpensesContextType | undefined>(undefined);

export const SuggestedExpensesProvider: React.FC<PropsWithChildren<{ tripId: string }>> = ({ children, tripId }) => {
  const { createExpense } = useExpenses();
  const nav = useNavigate();

  const [suggestedExpenses, setSuggestedExpenses] = useState<SuggestedExpense[]>([]);
  const [currentIndex, setCurrentIndex] = useState(0);
  const [loading, setLoading] = useState(false);

  const currentExpense = suggestedExpenses[currentIndex];
  const totalItems = suggestedExpenses.length;

  const setSuggestedFromReceipts = useCallback((receipts: ProcessingReceipt[]) => {
    setSuggestedExpenses(prev => {
      // Build map of existing isAdded state
      const existingState = new Map(prev.map(e => [e.id, e.isAdded]));

      const expenses: SuggestedExpense[] = [];
      receipts.forEach(receipt => {
        if (receipt.status !== 'completed' || !receipt.extractedData) return;

        if (receipt.extractedData.has_multiple_items && receipt.extractedData.items) {
          receipt.extractedData.items.forEach((item, idx) => {
            const id = `${receipt.id}-${idx}`;
            expenses.push({
              id,
              data: item,
              receiptUrl: receipt.uploadedUrl,
              originalFileName: receipt.file.name,
              isAdded: existingState.get(id) ?? false,
            });
          });
        } else {
          const id = `${receipt.id}-0`;
          expenses.push({
            id,
            data: receipt.extractedData,
            receiptUrl: receipt.uploadedUrl,
            originalFileName: receipt.file.name,
            isAdded: existingState.get(id) ?? false,
          });
        }
      });

      // Reset index when list is brand new OR structure changed (e.g. merge reduced items)
      if (prev.length === 0 || expenses.length !== prev.length) {
        // Clamp index to new bounds, default to first unadded
        const firstUnadded = expenses.findIndex(e => !e.isAdded);
        setCurrentIndex(firstUnadded !== -1 ? firstUnadded : 0);
      }

      return expenses;
    });
  }, []);

  const navigate = useCallback((direction: 'prev' | 'next') => {
    setCurrentIndex(prev => {
      if (direction === 'prev') return Math.max(0, prev - 1);
      return Math.min(suggestedExpenses.length - 1, prev + 1);
    });
  }, [suggestedExpenses.length]);

  const goTo = useCallback((index: number) => {
    setCurrentIndex(Math.max(0, Math.min(suggestedExpenses.length - 1, index)));
  }, [suggestedExpenses.length]);

  const addExpense = useCallback(async (id: string, formData: ExpenseFormData) => {
    setLoading(true);
    try {
      await createExpense({
        trip_id: formData.tripId,
        category: formData.category,
        description: formData.description,
        amount: formData.amount,
        base_amount: formData.base_amount || undefined,
        tax: formData.tax || undefined,
        tip: formData.tip || undefined,
        fees: formData.fees || undefined,
        currency: formData.currency,
        expense_date: formData.expense_date,
        is_planned: formData.is_planned,
        merchant: formData.merchant || undefined,
        notes: formData.notes || undefined,
        receipt_url: formData.receipt_url,
        original_file_name: formData.original_file_name,
      });

      // Mark as added and navigate to next unadded
      setSuggestedExpenses(prev => {
        const updated = prev.map(e => e.id === id ? { ...e, isAdded: true } : e);

        // Find next unadded expense after current
        setCurrentIndex(currIdx => {
          const nextUnadded = updated.findIndex((e, idx) => idx > currIdx && !e.isAdded);
          if (nextUnadded !== -1) return nextUnadded;

          // All added? Navigate back to trip
          const allAdded = updated.every(e => e.isAdded);
          if (allAdded) {
            setTimeout(() => nav(`/trip/${tripId}`), 500);
            return currIdx;
          }

          // Move forward if possible
          return Math.min(currIdx + 1, updated.length - 1);
        });

        return updated;
      });
    } catch (error) {
      console.error('Error creating expense:', error);
    } finally {
      setLoading(false);
    }
  }, [createExpense, nav, tripId]);

  const addAllExpenses = useCallback(async () => {
    setLoading(true);
    try {
      const unadded = suggestedExpenses.filter(e => !e.isAdded);
      await Promise.all(
        unadded.map(expense =>
          createExpense({
            trip_id: tripId,
            category: expense.data.category || 'Food',
            description: expense.data.description || '',
            amount: expense.data.amount || 0,
            base_amount: expense.data.base_amount || undefined,
            tax: expense.data.tax || undefined,
            tip: expense.data.tip || undefined,
            fees: expense.data.fees || undefined,
            currency: expense.data.currency || 'USD',
            expense_date: expense.data.expense_date || new Date().toISOString().split('T')[0],
            is_planned: false,
            merchant: expense.data.merchant || undefined,
            receipt_url: expense.receiptUrl,
            original_file_name: expense.originalFileName,
          })
        )
      );
      setSuggestedExpenses(prev => prev.map(e => ({ ...e, isAdded: true })));
      setTimeout(() => {
        nav(`/trip/${tripId}`);
      }, 500);
    } catch (error) {
      console.error('Error creating all expenses:', error);
    } finally {
      setLoading(false);
    }
  }, [suggestedExpenses, createExpense, tripId, nav]);

  const clear = useCallback(() => {
    setSuggestedExpenses([]);
    setCurrentIndex(0);
  }, []);

  const value = useMemo<SuggestedExpensesContextType>(() => ({
    suggestedExpenses,
    currentIndex,
    currentExpense,
    totalItems,
    loading,
    navigate,
    goTo,
    addExpense,
    addAllExpenses,
    setSuggestedFromReceipts,
    clear,
  }), [suggestedExpenses, currentIndex, currentExpense, totalItems, loading, navigate, goTo, addExpense, addAllExpenses, setSuggestedFromReceipts, clear]);

  return (
    <SuggestedExpensesContext.Provider value={value}>
      {children}
    </SuggestedExpensesContext.Provider>
  );
};

export const useSuggestedExpenses = () => {
  const context = useContext(SuggestedExpensesContext);
  if (context === undefined) {
    throw new Error('useSuggestedExpenses must be used within SuggestedExpensesProvider');
  }
  return context;
};
